/**
 * Bootstrap validation phase — pure state helper.
 *
 * The customer app persists a session snapshot in localStorage
 * (`ev.session.v1`) plus a lightweight recovery beacon
 * (`ev_active_session_uid`). Historically the bootstrap effect in
 * `App.jsx` rendered the charging step derived from that snapshot
 * *while* the backend rehydration was still in flight (and — worse —
 * treated the local snapshot as authoritative when the backend was
 * transiently unreachable). That let stale storage restore an
 * expired session, or make the app claim "charging" during an
 * offline window.
 *
 * This module is the single source of truth for **which UI to
 * render while validation runs / after it fails**. It's a pure
 * function so we can exhaustively lock every outcome via unit
 * tests without spinning up React.
 *
 *   VALIDATING    — backend request in flight; show a validating
 *                   splash. Never render the charging Screen.
 *   READY         — validation succeeded, OR completed with a
 *                   non-recoverable outcome and the caller has
 *                   already cleaned up state. Safe to render the
 *                   current Screen.
 *   RECONNECTING  — backend is transiently unreachable. Local
 *                   snapshot is treated as a *hint only* — the UI
 *                   surfaces a dedicated banner + disables the
 *                   charging control affordances. Retry loops in
 *                   the caller flip this back to READY once a
 *                   backend response arrives.
 *
 * Terminal reasons: every non-transient failure returned by
 * `SessionContext.rehydrateSession` collapses to READY here, because
 * the bootstrap caller uses the same set to trigger local cleanup
 * (URL + beacon + session state) before we ever get here. The set is
 * exported so the App.jsx orchestrator can share it.
 */

export const VALIDATION_PHASES = Object.freeze({
  IDLE: 'idle',
  VALIDATING: 'validating',
  READY: 'ready',
  RECONNECTING: 'reconnecting',
});

/**
 * Every rehydrate reason we treat as "the local snapshot is not
 * recoverable — clean up and let the user land on a fresh screen".
 *
 * Kept as a Set so both the phase resolver and the App.jsx cleanup
 * branch consult the same list. Historically the cleanup branch
 * matched only three of these (`not_found`, `session_completed`,
 * `session_terminal_state`), which meant a stale-pending / stale-
 * authorized / stale-charging rehydrate would `reset()` local state
 * but leave a `?sessionUid=…` param in the URL and the recovery
 * beacon untouched — the next reload would then try to recover the
 * same dead session again.
 */
export const TERMINAL_REHYDRATE_REASONS = Object.freeze(new Set([
  // Backend definitively says no such session.
  'not_found',
  // Backend returned something we couldn't parse — treat as gone.
  'invalid_session',
  // Backend returned a terminal status (failed / voided / expired
  // / cancelled) and rehydrate reset() us.
  'session_terminal_state',
  // Session was completed/finalized but the receipt window has
  // elapsed (>24 h) — see rehydrateSession stale-receipt guard.
  'stale_completed_receipt',
  // Age-gated rejections for non-terminal rows.
  'stale_pending_session',
  'stale_authorized_session',
  'stale_charging_session',
  // Uncaught exception during rehydration — treat like not_found.
  'exception',
]));

export function isTerminalRehydrateReason(reason) {
  return TERMINAL_REHYDRATE_REASONS.has(reason);
}

/**
 * Resolve which validation phase the UI should render given a
 * rehydrate result.
 *
 * @param {object|null} result - The value returned by
 *   `SessionContext.rehydrateSession`. Shape:
 *     { success: true, ... }                      — happy path
 *     { success: false, transient: true, ... }    — network / 5xx
 *     { success: false, reason: '…' }             — terminal / stale
 *   `null` means "no rehydration attempted" (e.g. no recovery hint).
 * @returns {'ready' | 'reconnecting'}
 */
export function resolveValidationPhase(result) {
  if (!result) return VALIDATION_PHASES.READY;
  if (result.success) return VALIDATION_PHASES.READY;
  if (result.transient === true) return VALIDATION_PHASES.RECONNECTING;
  // Any non-transient failure is either a clean-up-already-done case
  // (terminal / not_found / stale) or an unknown-but-definitive one.
  // Both are safe to render as a fresh start screen.
  return VALIDATION_PHASES.READY;
}

/**
 * Backoff schedule (ms) for the reconnect retry loop. Small at first
 * so a flaky connection recovers quickly, then longer so we don't
 * hammer the API during a real outage. The final entry repeats.
 */
export const RECONNECT_BACKOFF_MS = Object.freeze([
  2000,   //  2 s
  4000,   //  4 s
  8000,   //  8 s
  15000,  // 15 s
  30000,  // 30 s (repeats)
]);

/**
 * Pick the delay for the next reconnect attempt. `attempt` is
 * zero-indexed; delays past the end of the schedule reuse the last
 * entry so retries continue at a steady cadence indefinitely.
 */
export function reconnectDelayFor(attempt) {
  if (!Number.isFinite(attempt) || attempt < 0) return RECONNECT_BACKOFF_MS[0];
  const idx = Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1);
  return RECONNECT_BACKOFF_MS[idx];
}
